import { createRouter, createWebHistory } from 'vue-router'
import { clearPendingCreateAlbumId } from '@/constants/albumCreateGate'
import { clearPendingCreateActivityId } from '@/constants/eventCreateGate'
import { RBAC } from '@/constants/rbacPermissions'
import { getPermissionNamesFromStorage, checkRouteAccess, REGISTRATION_NAV_PERMISSIONS } from '@/utils/rbac'
import { syncDocumentTitle } from '@/utils/syncDocumentTitle'
import { getParticipantToken } from '@/constants/participantAuth'
import { resolveRoutePermissionMeta, isSuperAdminOnlyRoute } from '@/utils/rbacRouteGuard'
import { resolveIsSuperAdminFromStorage } from '@/constants/superAdminRoles'
import { isStoredProxyUser, resolvePostLoginRedirect } from '@/utils/proxyAuth'

const EventSmsNotificationsView = () => import('@/views/conference/EventSmsNotificationsView.vue')

/**
 * 路由配置
 * meta.requiresAuth = true  需要登录才能访问
 * meta.guest = true         已登录用户不可访问（登录/注册页）
 * meta.permission           访问所需子权限名（string 或 string[]，数组为任一即可）
 */
const routes = [
  { path: '/vote/:id/results', name: 'PublicVoteResults', component: () => import('@/views/conference/PublicVoteResultsView.vue'), meta: { title: '投票排行榜', requiresParticipantAuth: true } },
  { path: '/vote/:id', name: 'PublicVote', component: () => import('@/views/conference/PublicVoteView.vue'), meta: { title: '投票', requiresParticipantAuth: true } },
  { path: '/', redirect: '/login' },

  // ==================== 认证页面 ====================
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/auth/LoginView.vue'),
    meta: { titleKey: 'route.login', guest: true }
  },
  {
    path: '/register',
    name: 'Register',
    component: () => import('@/views/auth/RegisterView.vue'),
    meta: { titleKey: 'route.register', guest: true }
  },
  {
    path: '/forgot-password',
    name: 'ForgotPassword',
    component: () => import('@/views/auth/ForgotPasswordView.vue'),
    meta: { titleKey: 'route.forgotPassword', guest: true }
  },
  {
    // 重置密码页：由邮件链接跳转，URL 携带 ?token=xxx&email=xxx
    path: '/reset-password',
    name: 'ResetPassword',
    component: () => import('@/views/auth/ResetPasswordView.vue'),
    meta: { titleKey: 'route.resetPassword', guest: true }
  },

  {
    // 微信 OAuth 回调
    path: '/login/wechat/callback',
    name: 'WechatCallback',
    component: () => import('@/views/auth/WechatCallbackView.vue'),
    meta: { titleKey: 'route.login', guest: true }
  },

  {
    // 旧邮件 / 桥接路径兼容 → 登录页带 token
    path: '/auth/proxy-bridge',
    redirect: (to) => ({ path: '/login', query: to.query })
  },
  {
    path: '/auth/proxy-grant',
    redirect: (to) => ({ path: '/login', query: to.query })
  },
  {
    path: '/proxy/consume',
    redirect: (to) => ({ path: '/login', query: to.query })
  },
  {
    path: '/proxy/entry-hint',
    name: 'ProxyEntryHint',
    component: () => import('@/views/auth/ProxyEntryHintView.vue'),
    meta: { titleKey: 'route.proxyEntryHint', requiresAuth: true, permission: RBAC.REGISTRATION_LIST }
  },

  // ==================== C 端参会用户认证 ====================
  {
    path: '/c/login',
    name: 'ParticipantLogin',
    component: () => import('@/views/participant/ParticipantLoginView.vue'),
    meta: { titleKey: 'route.participantLogin', participantGuest: true }
  },
  {
    path: '/c/register',
    name: 'ParticipantRegister',
    component: () => import('@/views/participant/ParticipantRegisterView.vue'),
    meta: { titleKey: 'route.participantRegister', participantGuest: true }
  },
  {
    path: '/c/email/verify',
    name: 'ParticipantEmailVerify',
    component: () => import('@/views/participant/ParticipantEmailVerifyView.vue'),
    meta: { titleKey: 'route.participantEmailVerify', participantGuest: true }
  },

  // ==================== 登录后页面 ====================
  {
    path: '/dashboard',
    component: () => import('@/layouts/MainLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'Dashboard',
        component: () => import('@/views/dashboard/DashboardView.vue'),
        meta: { titleKey: 'route.home', requiresAuth: true, permission: RBAC.ALBUM_LIST }
      },
      {
        path: 'forbidden',
        name: 'Forbidden',
        component: () => import('@/views/auth/ForbiddenView.vue'),
        meta: { titleKey: 'route.forbidden', requiresAuth: true }
      },
      {
        path: 'accounts',
        name: 'Accounts',
        component: () => import('@/views/dashboard/AccountView.vue'),
        meta: { titleKey: 'route.accountManage', requiresAuth: true, permission: RBAC.ACCOUNT_LIST }
      },
      {
        path: 'operation-logs',
        name: 'OperationLogs',
        component: () => import('@/views/dashboard/OperationLogsView.vue'),
        meta: { title: '操作日志', requiresAuth: true }
      },
      {
        path: 'personal-center',
        name: 'PersonalCenter',
        component: () => import('@/views/dashboard/PersonalCenterView.vue'),
        meta: { titleKey: 'route.personalCenter', requiresAuth: true }
      },
      {
        path: 'inbox/messages',
        name: 'InboxMessages',
        component: () => import('@/views/dashboard/InboxMessagesView.vue'),
        meta: { titleKey: 'route.messageNotifications', requiresAuth: true },
        props: { variant: 'user' }
      },
      {
        path: 'inbox/todos',
        name: 'InboxTodos',
        component: () => import('@/views/dashboard/InboxMessagesView.vue'),
        meta: { titleKey: 'route.todoTasks', requiresAuth: true, superAdminOnly: true },
        props: { variant: 'admin' }
      },
      {
        path: 'permissions',
        name: 'Permissions',
        component: () => import('@/views/dashboard/PermissionView.vue'),
        meta: { titleKey: 'route.permissionManage', requiresAuth: true, permission: RBAC.PERMISSION_LIST }
      },
      {
        path: 'roles',
        name: 'Roles',
        component: () => import('@/views/dashboard/RoleView.vue'),
        meta: { titleKey: 'route.roleManage', requiresAuth: true, permission: RBAC.PERMISSION_LIST }
      },
      {
        path: 'admin/company-plan-overview',
        name: 'CompanyPlanOverview',
        component: () => import('@/views/dashboard/CompanyPlanOverviewView.vue'),
        meta: { titleKey: 'route.memberDataOverview', requiresAuth: true, sidebar: 'memberManage', permission: RBAC.PLAN_ADMIN_LIST }
      },
      {
        path: 'admin/company-plans',
        name: 'CompanyPlanManage',
        component: () => import('@/views/dashboard/CompanyPlanManageView.vue'),
        meta: { titleKey: 'route.companyPlanManage', requiresAuth: true, sidebar: 'memberManage', permission: RBAC.PLAN_ADMIN_LIST }
      },
      {
        path: 'admin/company-plan-orders',
        name: 'CompanyPlanOrderManage',
        component: () => import('@/views/dashboard/CompanyPlanOrderManageView.vue'),
        meta: { titleKey: 'route.companyPlanOrders', requiresAuth: true, sidebar: 'memberManage', permission: RBAC.PLAN_ADMIN_ORDER_LIST }
      },
      {
        path: 'admin/recharge-overview',
        name: 'AdminRechargeOverview',
        component: () => import('@/views/dashboard/AdminRechargeOverviewView.vue'),
        meta: { titleKey: 'route.adminRechargeOverview', requiresAuth: true, sidebar: 'rechargeManage', permission: RBAC.RECHARGE_ADMIN_OVERVIEW }
      },
      {
        path: 'admin/recharge-resource-types',
        name: 'RechargeResourceManage',
        component: () => import('@/views/dashboard/RechargeResourceManageView.vue'),
        meta: { titleKey: 'route.rechargeResourceManage', requiresAuth: true, sidebar: 'rechargeManage', permission: RBAC.RECHARGE_ADMIN_TYPE_LIST }
      },
      {
        path: 'admin/recharge-records',
        name: 'AdminRechargeRecords',
        component: () => import('@/views/dashboard/AdminRechargeRecordsView.vue'),
        meta: { titleKey: 'route.adminRechargeRecords', requiresAuth: true, sidebar: 'rechargeManage', permission: RBAC.RECHARGE_ADMIN_ORDER_LIST }
      },
      {
        path: 'admin/members',
        name: 'AdminMemberList',
        component: () => import('@/views/dashboard/AdminMemberListView.vue'),
        meta: { titleKey: 'route.userList', requiresAuth: true, sidebar: 'userManage', permission: RBAC.COMPANY_LIST }
      },
      {
        path: 'admin/members/:id',
        name: 'AdminMemberDetail',
        component: () => import('@/views/dashboard/AdminMemberDetailView.vue'),
        meta: { titleKey: 'route.userDetail', requiresAuth: true, sidebar: 'userManage', permission: RBAC.COMPANY_LIST }
      },
      {
        path: 'admin/user-verification-config',
        name: 'UserVerificationConfig',
        component: () => import('@/views/dashboard/UserVerificationConfigView.vue'),
        meta: { titleKey: 'route.userVerificationConfig', requiresAuth: true, sidebar: 'userManage', permission: RBAC.COMPANY_LIST }
      },
      {
        path: 'admin/user-verification-reviews',
        name: 'UserVerificationReviews',
        component: () => import('@/views/dashboard/UserVerificationReviewView.vue'),
        meta: { titleKey: 'route.userVerificationReviews', requiresAuth: true, sidebar: 'userManage', permission: RBAC.COMPANY_LIST }
      },
      {
        path: 'admin/help',
        name: 'HelpManage',
        component: () => import('@/views/dashboard/HelpManageView.vue'),
        meta: { titleKey: 'route.helpManage', requiresAuth: true, superAdminOnly: true }
      },
      {
        path: 'help-center',
        name: 'HelpCenter',
        component: () => import('@/views/dashboard/HelpCenterView.vue'),
        meta: {
          titleKey: 'route.helpCenter',
          requiresAuth: true,
          hideSidebar: true,
          contentFill: 'inset',
          helpCenter: true
        }
      },
      {
        path: 'help-center/category/:categoryId(\\d+)',
        name: 'HelpCategory',
        component: () => import('@/views/dashboard/HelpCategoryView.vue'),
        meta: {
          titleKey: 'route.helpCenter',
          requiresAuth: true,
          hideSidebar: true,
          contentFill: 'inset',
          helpCenter: true
        }
      },
      {
        path: 'admin/help/create',
        name: 'HelpCreate',
        component: () => import('@/views/dashboard/HelpCreateView.vue'),
        meta: { titleKey: 'route.helpCreate', requiresAuth: true, superAdminOnly: true }
      },
      {
        path: 'admin/help/:id/edit',
        name: 'HelpEdit',
        component: () => import('@/views/dashboard/HelpCreateView.vue'),
        meta: { titleKey: 'route.helpEdit', requiresAuth: true, superAdminOnly: true }
      },
      {
        path: 'help-center',
        name: 'HelpCenter',
        component: () => import('@/views/dashboard/HelpCenterView.vue'),
        meta: {
          titleKey: 'route.helpCenter',
          requiresAuth: true,
          hideSidebar: true,
          contentFill: 'inset',
          helpCenter: true
        }
      },
      {
        path: 'help-center/category/:categoryId(\\d+)',
        name: 'HelpCategory',
        component: () => import('@/views/dashboard/HelpCategoryView.vue'),
        meta: {
          titleKey: 'route.helpCenter',
          requiresAuth: true,
          hideSidebar: true,
          contentFill: 'inset',
          helpCenter: true
        }
      },
      {
        path: 'member-plans',
        name: 'MemberPlans',
        component: () => import('@/views/dashboard/MemberPlanView.vue'),
        meta: { titleKey: 'route.memberPlanDetail', requiresAuth: true, permission: RBAC.PLAN_CURRENT }
      },
      {
        path: 'member-orders',
        redirect: { name: 'MemberPlans', query: { tab: 'orders' } },
        meta: { requiresAuth: true, permission: RBAC.PLAN_ORDER_LIST }
      },
      {
        path: 'recharge-packages',
        name: 'RechargePackages',
        component: () => import('@/views/dashboard/RechargePackagesView.vue'),
        meta: { titleKey: 'route.rechargePackages', requiresAuth: true, sidebar: 'rechargePackages', permission: RBAC.RECHARGE_CATALOG }
      },
      {
        path: 'recharge-packs',
        name: 'RechargeMyPacks',
        component: () => import('@/views/dashboard/RechargeMyPacksView.vue'),
        meta: { titleKey: 'route.rechargeMyPacks', requiresAuth: true, sidebar: 'rechargePackages', permission: RBAC.RECHARGE_CREDITS }
      },
      {
        path: 'recharge-records',
        name: 'RechargeRecords',
        component: () => import('@/views/dashboard/RechargeRecordsView.vue'),
        meta: { titleKey: 'route.rechargeRecords', requiresAuth: true, sidebar: 'rechargePackages', permission: RBAC.RECHARGE_ORDER_LIST }
      },
      {
        path: 'cloud-space',
        name: 'CloudSpace',
        component: () => import('@/views/dashboard/CloudSpaceView.vue'),
        meta: { titleKey: 'route.cloudSpace', requiresAuth: true, permission: RBAC.PLAN_CURRENT }
      },
      {
        path: 'admin/cloud-space',
        name: 'AdminCloudSpace',
        component: () => import('@/views/dashboard/AdminCloudSpaceView.vue'),
        meta: { titleKey: 'route.adminCloudSpace', requiresAuth: true, permission: RBAC.PLAN_CLOUD_SPACE_ADMIN }
      },
      {
        path: 'admin/resource-grants',
        name: 'ResourceGrantManage',
        component: () => import('@/views/dashboard/ResourceGrantManageView.vue'),
        meta: { titleKey: 'route.resourceGrantManage', requiresAuth: true, permission: RBAC.RESOURCE_GRANT_LIST }
      },
      {
        // 会务系统 —— 活动列表
        path: 'conference/events',
        name: 'ConferenceEvents',
        component: () => import('@/views/conference/EventManageView.vue'),
        meta: { titleKey: 'route.eventManage', requiresAuth: true, permission: RBAC.ACTIVITY_LIST }
      },
      {
        // 新建活动 —— 活动信息（首步 POST 创建）
        path: 'conference/events/create',
        name: 'EventCreate',
        component: () => import('@/views/conference/EventInfoView.vue'),
        meta: { titleKey: 'route.eventCreate', requiresAuth: true, sidebar: 'event', permission: RBAC.ACTIVITY_CREATE }
      },
      {
        path: 'conference/events/create/location',
        redirect: '/dashboard/conference/events/create'
      },
      {
        // 新建活动 —— 报名配置
        path: 'conference/events/create/registration',
        name: 'EventCreateRegistration',
        component: () => import('@/views/conference/EventRegistrationSetupView.vue'),
        meta: {
          titleKey: 'route.eventRegistrationSetup',
          requiresAuth: true,
          sidebar: 'event',
          permission: REGISTRATION_NAV_PERMISSIONS
        }
      },
      {
        // 新建活动 —— 会议日程
        path: 'conference/events/create/schedule',
        name: 'EventCreateSchedule',
        component: () => import('@/views/conference/EventScheduleView.vue'),
        meta: {
          titleKey: 'route.eventSchedule',
          requiresAuth: true,
          sidebar: 'event',
          permission: RBAC.ACTIVITY_SCHEDULE_LIST
        }
      },
      {
        // 新建活动 —— 座位分配记录
        path: 'conference/events/create/seating',
        name: 'EventCreateSeating',
        component: () => import('@/views/conference/EventSeatingView.vue'),
        meta: {
          titleKey: 'route.eventSeating',
          requiresAuth: true,
          sidebar: 'event',
          permission: RBAC.ACTIVITY_SEAT_LIST
        }
      },
      {
        path: 'conference/events/create/hotel/query-editor',
        name: 'EventCreateHotelQueryEditor',
        component: () => import('@/views/conference/EventHotelQueryEditorView.vue'),
        meta: { title: '酒店查询页配置', requiresAuth: true, sidebar: 'event', contentFill: 'inset', permission: [RBAC.ACTIVITY_EDIT, RBAC.HOTEL_LIST] }
      },
      {
        // 新建活动 —— 座位查询页编辑器
        path: 'conference/events/create/seating/editor',
        name: 'EventCreateSeatQueryEditor',
        component: () => import('@/views/conference/EventSeatQueryEditorView.vue'),
        meta: {
          titleKey: 'route.eventSeatQueryEditor',
          requiresAuth: true,
          sidebar: 'event',
          contentFill: 'inset',
          permission: [RBAC.ACTIVITY_SEAT_QUERY_VIEW, RBAC.ACTIVITY_SEAT_LIST]
        }
      },
      {
        // 新建活动 —— 活动分享模板（海报预览/下载，无后端）
        path: 'conference/events/create/share',
        name: 'EventCreateShare',
        component: () => import('@/views/conference/EventShareTemplateView.vue'),
        meta: {
          titleKey: 'route.eventShareTemplate',
          requiresAuth: true,
          sidebar: 'event',
          // 新叶子「配置活动分享模板」；兼容「创建活动」（向导内页）
          permission: [RBAC.ACTIVITY_SHARE_TEMPLATE, RBAC.ACTIVITY_CREATE]
        }
      },
      {
        path: 'conference/events/create/checkin',
        name: 'EventCreateCheckin',
        component: () => import('@/views/conference/EventCheckinView.vue'),
        meta: {
          titleKey: 'route.eventCheckin',
          requiresAuth: true,
          sidebar: 'event',
          contentFill: 'inset',
          permission: [RBAC.ACTIVITY_CHECKIN_VIEW, RBAC.ACTIVITY_EDIT]
        }
      },
      {
        path: 'conference/events/create/tasks',
        name: 'EventCreateTasks',
        component: () => import('@/views/conference/EventTaskManagementView.vue'),
        meta: { titleKey: 'route.eventTaskManagement', requiresAuth: true, sidebar: 'event', permission: RBAC.ACTIVITY_TASK_PEOPLE_LIST }
      },
      {
        path: 'conference/events/create/sms-notifications',
        name: 'EventCreateSmsNotifications',
        component: EventSmsNotificationsView,
        meta: { titleKey: 'route.eventSmsNotifications', requiresAuth: true, sidebar: 'event', permission: RBAC.ACTIVITY_CREATE }
      },
      {
        path: 'conference/events/:id(\\d+)/edit',
        name: 'EventEdit',
        component: () => import('@/views/conference/EventInfoView.vue'),
        meta: { titleKey: 'route.eventEdit', requiresAuth: true, sidebar: 'event', permission: RBAC.ACTIVITY_EDIT }
      },
      {
        path: 'conference/events/:id(\\d+)/edit/location',
        redirect: (to) => `/dashboard/conference/events/${to.params.id}/edit`
      },
      {
        // 编辑活动 —— 报名配置
        path: 'conference/events/:id(\\d+)/edit/registration',
        name: 'EventEditRegistration',
        component: () => import('@/views/conference/EventRegistrationSetupView.vue'),
        meta: {
          titleKey: 'route.eventRegistrationSetup',
          requiresAuth: true,
          sidebar: 'event',
          permission: REGISTRATION_NAV_PERMISSIONS
        }
      },
      {
        // 编辑活动 —— 会议日程
        path: 'conference/events/:id(\\d+)/edit/schedule',
        name: 'EventEditSchedule',
        component: () => import('@/views/conference/EventScheduleView.vue'),
        meta: {
          titleKey: 'route.eventSchedule',
          requiresAuth: true,
          sidebar: 'event',
          permission: RBAC.ACTIVITY_SCHEDULE_LIST
        }
      },
      {
        // 编辑活动 —— 座位分配记录
        path: 'conference/events/:id(\\d+)/edit/seating',
        name: 'EventEditSeating',
        component: () => import('@/views/conference/EventSeatingView.vue'),
        meta: {
          titleKey: 'route.eventSeating',
          requiresAuth: true,
          sidebar: 'event',
          permission: RBAC.ACTIVITY_SEAT_LIST
        }
      },
      {
        // 新建活动 —— 酒店配置（活动创建后才可实际维护）
        path: 'conference/events/create/hotel',
        name: 'EventCreateHotelConfig',
        component: () => import('@/views/conference/EventHotelConfigView.vue'),
        meta: {
          titleKey: 'route.hotelManage',
          requiresAuth: true,
          sidebar: 'event',
          permission: [RBAC.ACTIVITY_EDIT, RBAC.HOTEL_LIST]
        }
      },
      {
        // 编辑活动 —— 酒店配置
        path: 'conference/events/:id(\\d+)/edit/hotel',
        name: 'EventEditHotelConfig',
        component: () => import('@/views/conference/EventHotelConfigView.vue'),
        meta: {
          titleKey: 'route.hotelManage',
          requiresAuth: true,
          sidebar: 'event',
          permission: [RBAC.ACTIVITY_EDIT, RBAC.HOTEL_LIST]
        }
      },
      {
        path: 'conference/events/:id(\\d+)/edit/checkin',
        name: 'EventEditCheckin',
        component: () => import('@/views/conference/EventCheckinView.vue'),
        meta: {
          titleKey: 'route.eventCheckin',
          requiresAuth: true,
          sidebar: 'event',
          contentFill: 'inset',
          permission: [RBAC.ACTIVITY_CHECKIN_VIEW, RBAC.ACTIVITY_EDIT]
        }
      },
      {
        path: 'conference/events/:id(\\d+)/edit/tasks',
        name: 'EventEditTasks',
        component: () => import('@/views/conference/EventTaskManagementView.vue'),
        meta: { titleKey: 'route.eventTaskManagement', requiresAuth: true, sidebar: 'event', permission: RBAC.ACTIVITY_TASK_PEOPLE_LIST }
      },
      {
        path: 'conference/events/:id(\\d+)/edit/sms-notifications',
        name: 'EventEditSmsNotifications',
        component: EventSmsNotificationsView,
        meta: { titleKey: 'route.eventSmsNotifications', requiresAuth: true, sidebar: 'event', permission: RBAC.ACTIVITY_EDIT }
      },
      {
        path: 'conference/events/:id(\\d+)/edit/hotel/query-editor',
        name: 'EventEditHotelQueryEditor',
        component: () => import('@/views/conference/EventHotelQueryEditorView.vue'),
        meta: { title: '酒店查询页配置', requiresAuth: true, sidebar: 'event', contentFill: 'inset', permission: [RBAC.ACTIVITY_EDIT, RBAC.HOTEL_LIST] }
      },
      {
        // 编辑活动 —— 座位查询页编辑器
        path: 'conference/events/:id(\\d+)/edit/seating/editor',
        name: 'EventEditSeatQueryEditor',
        component: () => import('@/views/conference/EventSeatQueryEditorView.vue'),
        meta: {
          titleKey: 'route.eventSeatQueryEditor',
          requiresAuth: true,
          sidebar: 'event',
          contentFill: 'inset',
          permission: [RBAC.ACTIVITY_SEAT_QUERY_VIEW, RBAC.ACTIVITY_SEAT_LIST]
        }
      },
      {
        // 编辑活动 —— 活动分享模板
        path: 'conference/events/:id(\\d+)/edit/share',
        name: 'EventEditShare',
        component: () => import('@/views/conference/EventShareTemplateView.vue'),
        meta: {
          titleKey: 'route.eventShareTemplate',
          requiresAuth: true,
          sidebar: 'event',
          permission: [RBAC.ACTIVITY_SHARE_TEMPLATE, RBAC.ACTIVITY_EDIT]
        }
      },
      {
        // 新建活动 —— 微站 / 装修（页面列表）
        path: 'conference/events/create/microsite',
        name: 'EventCreateMicrosite',
        component: () => import('@/views/conference/EventMicrositeView.vue'),
        meta: {
          titleKey: 'route.eventMicrosite',
          requiresAuth: true,
          sidebar: 'event',
          permission: RBAC.ACTIVITY_MICROSITE_PAGES_LIST
        }
      },
      {
        // 新建活动 —— 微站单页编辑器
        path: 'conference/events/create/microsite/pages/:pageId/edit',
        name: 'EventCreateMicrositePageEdit',
        component: () => import('@/views/conference/EventMicrositeEditorView.vue'),
        meta: {
          titleKey: 'route.eventMicrositeEditor',
          requiresAuth: true,
          sidebar: 'event',
          contentFill: 'inset',
          permission: RBAC.ACTIVITY_MICROSITE_PAGE_VIEW
        }
      },
      {
        // 编辑活动 —— 微站 / 装修（页面列表）
        path: 'conference/events/:id(\\d+)/edit/microsite',
        name: 'EventEditMicrosite',
        component: () => import('@/views/conference/EventMicrositeView.vue'),
        meta: {
          titleKey: 'route.eventMicrosite',
          requiresAuth: true,
          sidebar: 'event',
          permission: RBAC.ACTIVITY_MICROSITE_PAGES_LIST
        }
      },
      {
        path: 'conference/events/:id(\\d+)/edit/microsite/registration/edit',
        name: 'EventRegistrationPageDecor',
        component: () => import('@/views/conference/EventRegistrationPageDecorView.vue'),
        meta: {
          titleKey: 'route.eventMicrositeEditor',
          requiresAuth: true,
          sidebar: 'event',
          contentFill: 'inset',
          permission: RBAC.ACTIVITY_MICROSITE_PAGE_VIEW
        }
      },
      {
        // 编辑活动 —— 微站单页编辑器
        path: 'conference/events/:id(\\d+)/edit/microsite/pages/:pageId/edit',
        name: 'EventEditMicrositePageEdit',
        component: () => import('@/views/conference/EventMicrositeEditorView.vue'),
        meta: {
          titleKey: 'route.eventMicrositeEditor',
          requiresAuth: true,
          sidebar: 'event',
          contentFill: 'inset',
          permission: RBAC.ACTIVITY_MICROSITE_PAGE_VIEW
        }
      },
      {
        // 受邀参与活动 —— 默认进活动官网
        path: 'conference/events/:id(\\d+)/invited',
        redirect: (to) => `/dashboard/conference/events/${to.params.id}/invited/intro`
      },
      {
        path: 'conference/events/:id(\\d+)/invited/intro',
        name: 'InvitedActivityIntro',
        component: () => import('@/views/conference/invited/InvitedIntroView.vue'),
        meta: {
          titleKey: 'route.invitedIntro',
          requiresAuth: true,
          sidebar: 'invited',
          permission: RBAC.ACTIVITY_LIST
        }
      },
      {
        path: 'conference/events/:id(\\d+)/invited/agenda',
        name: 'InvitedActivityAgenda',
        component: () => import('@/views/conference/invited/InvitedAgendaView.vue'),
        meta: {
          titleKey: 'route.invitedAgenda',
          requiresAuth: true,
          sidebar: 'invited',
          permission: RBAC.ACTIVITY_LIST
        }
      },
      {
        path: 'conference/events/:id(\\d+)/invited/registration',
        name: 'InvitedActivityRegistration',
        component: () => import('@/views/conference/invited/InvitedRegistrationView.vue'),
        meta: {
          titleKey: 'route.invitedRegistration',
          requiresAuth: true,
          sidebar: 'invited',
          permission: RBAC.ACTIVITY_LIST
        }
      },
      {
        path: 'conference/events/:id(\\d+)/invited/records',
        redirect: (to) => ({
          name: 'InvitedActivityRegistration',
          params: { id: to.params.id }
        })
      },
      {
        path: 'conference/events/:id(\\d+)/invited/orders',
        name: 'InvitedActivityOrders',
        component: () => import('@/views/conference/invited/InvitedOrdersView.vue'),
        meta: {
          titleKey: 'route.invitedOrders',
          requiresAuth: true,
          sidebar: 'invited',
          permission: RBAC.ACTIVITY_LIST
        }
      },
      {
        path: 'conference/votes',
        name: 'ConferenceVotes',
        component: () => import('@/views/conference/VotingManageView.vue'),
        meta: { titleKey: 'route.votingManage', requiresAuth: true, permission: '查看投票管理' }
      },
        {
          path: 'sxa/instances',
          name: 'SxaInstanceEntry',
          component: () => import('@/views/dashboard/SxaInstanceEntryView.vue'),
          meta: { titleKey: 'nav.associationDashboard', requiresAuth: true, sidebar: 'associationManage', permission: RBAC.ASSOCIATION_MANAGE_VIEW }
      },
        {
          path: 'sxa/instances/:instance(\\d+)',
          name: 'SxaInstanceShell',
          component: () => import('@/views/dashboard/SxaInstanceShellView.vue'),
          meta: { titleKey: 'nav.associationDashboard', requiresAuth: true, sidebar: 'associationManage', permission: RBAC.ASSOCIATION_MANAGE_VIEW }
        },
        {
          path: 'sxa/instances/:instance(\\d+)/home-page',
          name: 'SxaHomePageEditor',
          component: () => import('@/views/dashboard/SxaHomePageEditorView.vue'),
          meta: { titleKey: 'nav.associationHomePage', requiresAuth: true, sidebar: 'associationManage', permission: RBAC.ASSOCIATION_HOME_EDIT }
        },
        {
          path: 'sxa/instances/:instance(\\d+)/mine-page',
          name: 'SxaMinePageEditor',
          component: () => import('@/views/dashboard/SxaMinePageEditorView.vue'),
          meta: { title: '个人中心装修', titleKey: 'nav.associationPersonalCenter', requiresAuth: true, sidebar: 'associationManage', permission: RBAC.ASSOCIATION_HOME_EDIT }
        },
        {
          path: 'sxa/instances/:instance(\\d+)/bottom-navigation',
          name: 'SxaBottomNavigation',
          component: () => import('@/views/dashboard/SxaBottomNavigationView.vue'),
          meta: { title: '底部导航', titleKey: 'nav.associationBottomNavigation', requiresAuth: true, sidebar: 'associationManage', permission: RBAC.ASSOCIATION_HOME_EDIT }
        },
        {
          path: 'sxa/instances/:instance(\\d+)/membership/fee-settings',
          name: 'SxaMembershipFeeSettings',
          component: () => import('@/views/dashboard/SxaMembershipFeeSettingsView.vue'),
          meta: { titleKey: 'nav.associationFeeSettings', requiresAuth: true, sidebar: 'associationManage', permission: RBAC.ASSOCIATION_FEE_SETTINGS }
        },
        {
          path: 'sxa/instances/:instance(\\d+)/membership/fee-settings/types/:type(\\d+)/fields',
          name: 'SxaMembershipRegistrationFields',
          component: () => import('@/views/dashboard/SxaMembershipRegistrationFieldsView.vue'),
          meta: { title: '入会登记字段配置', requiresAuth: true, sidebar: 'associationManage', permission: RBAC.ASSOCIATION_FEE_SETTINGS }
        },
        {
          path: 'sxa/instances/:instance(\\d+)/membership/applications',
          name: 'SxaMembershipApplications',
          component: () => import('@/views/dashboard/SxaMembershipApplicationsView.vue'),
          meta: { titleKey: 'nav.associationJoinApplication', requiresAuth: true, sidebar: 'associationManage', permission: RBAC.ASSOCIATION_FEE_SETTINGS }
        },
        { path: 'sxa/instances/:instance(\\d+)/membership/members', name: 'SxaMembershipMembers', component: () => import('@/views/dashboard/SxaMembershipMembersView.vue'), meta: { titleKey: 'nav.associationMemberList', requiresAuth: true, sidebar: 'associationManage', permission: RBAC.ASSOCIATION_FEE_SETTINGS } },
        {
          path: 'sxa/instances/:instance(\\d+)/membership/showcase',
          name: 'SxaMembershipShowcase',
          component: () => import('@/views/dashboard/SxaMembershipShowcaseView.vue'),
          meta: { titleKey: 'nav.associationMemberShowcase', requiresAuth: true, sidebar: 'associationManage', permission: RBAC.ASSOCIATION_FEE_SETTINGS }
        },
        {
          path: 'sxa/instances/:instance(\\d+)/membership/payment-methods',
          name: 'SxaMembershipPaymentMethods',
          component: () => import('@/views/dashboard/SxaMembershipPaymentMethodsView.vue'),
          meta: { titleKey: 'nav.associationPaymentMethods', requiresAuth: true, sidebar: 'associationManage', permission: RBAC.ASSOCIATION_FEE_SETTINGS }
        },
        {
          path: 'sxa/instances/:instance(\\d+)/membership/fee-orders',
          name: 'SxaMembershipFeeOrders',
          component: () => import('@/views/dashboard/SxaMembershipFeeOrdersView.vue'),
          meta: { titleKey: 'nav.associationFeeRecords', requiresAuth: true, sidebar: 'associationManage', permission: RBAC.ASSOCIATION_FEE_SETTINGS }
        },
        {
          path: 'sxa/instances/:instance(\\d+)/activities',
          name: 'SxaActivities',
          component: () => import('@/views/dashboard/SxaActivitiesView.vue'),
          meta: { title: '活动列表', titleKey: 'nav.associationActivityList', requiresAuth: true, sidebar: 'associationManage', permission: RBAC.ASSOCIATION_ACTIVITIES }
        },
        {
          path: 'sxa/instances/:instance(\\d+)/activities/:activityId(\\d+)/setup',
          name: 'SxaActivitySetup',
          component: () => import('@/views/dashboard/SxaActivitySetupView.vue'),
          meta: { title: '活动设置', titleKey: 'sxaActivities.activitySettings', requiresAuth: true, sidebar: 'associationManage', permission: RBAC.ASSOCIATION_ACTIVITIES }
        },
        {
          path: 'sxa/instances/:instance(\\d+)/activities/photo-live',
          redirect: (to) => ({ name: 'SxaActivities', params: { instance: to.params.instance } })
        },
        {
          path: 'sxa/instances/:instance(\\d+)/activities/registration',
          redirect: (to) => ({ name: 'SxaActivities', params: { instance: to.params.instance } })
        },
        {
          path: 'sxa/instances/:instance(\\d+)/activities/refunds',
          name: 'SxaActivityRefunds',
          component: () => import('@/views/dashboard/SxaActivityRefundsView.vue'),
          meta: { title: '活动退款', titleKey: 'nav.associationActivityRefund', requiresAuth: true, sidebar: 'associationManage', permission: RBAC.ASSOCIATION_ACTIVITIES }
        },
        {
          path: 'sxa/instances/:instance(\\d+)/demands',
          name: 'SxaDemands',
          component: () => import('@/views/dashboard/SxaDemandsView.vue'),
          meta: {
            titleKey: 'route.sxaDemands',
            requiresAuth: true,
            sidebar: 'associationManage',
            // 新权限「管理协会供需」；兼容尚未重新勾选的旧角色（已有活动/文章/看板权限即可进）
            permission: [
              RBAC.ASSOCIATION_DEMANDS,
              RBAC.ASSOCIATION_ACTIVITIES,
              RBAC.ASSOCIATION_ARTICLES,
              RBAC.ASSOCIATION_MANAGE_VIEW
            ]
          }
        },
        {
          path: 'sxa/instances/:instance(\\d+)/articles',
          name: 'SxaArticles',
          component: () => import('@/views/dashboard/SxaArticlesView.vue'),
          meta: { title: '文章管理', titleKey: 'nav.associationArticleList', requiresAuth: true, sidebar: 'associationManage', permission: RBAC.ASSOCIATION_ARTICLES }
        },
        {
          path: 'sxa/instances/:instance(\\d+)/article-categories',
          name: 'SxaArticleCategories',
          component: () => import('@/views/dashboard/SxaArticleCategoriesView.vue'),
          meta: { title: '文章分类', titleKey: 'nav.associationArticleCategories', requiresAuth: true, sidebar: 'associationManage', permission: RBAC.ASSOCIATION_ARTICLES }
        },
        {
          path: 'sxa/instances/:instance(\\d+)/association-profile',
          name: 'SxaAssociationProfile',
          component: () => import('@/views/dashboard/SxaAssociationProfileView.vue'),
          meta: { title: '商协配置', titleKey: 'nav.associationProfile', requiresAuth: true, sidebar: 'associationManage', permission: RBAC.ASSOCIATION_MINI_PROGRAM_CONFIG }
        },
        {
          path: 'sxa/instances/:instance(\\d+)/mini-program-config',
          name: 'SxaMiniProgramConfig',
          component: () => import('@/views/dashboard/SxaMiniProgramConfigView.vue'),
          meta: { title: '小程序配置', titleKey: 'nav.associationMiniProgramConfig', requiresAuth: true, sidebar: 'associationManage', permission: RBAC.ASSOCIATION_MINI_PROGRAM_CONFIG }
        },
      {
        path: 'admin/sxa/overview', name: 'SxaPlatformOverview', component: () => import('@/views/dashboard/SxaServiceOverviewView.vue'), meta: { titleKey: 'associationAdmin.overviewTitle', requiresAuth: true, sidebar: 'sxaPlatform', superAdminOnly: true }
      },
      {
        path: 'admin/sxa/plans', name: 'SxaServicePlans', component: () => import('@/views/dashboard/SxaServicePlanManageView.vue'), meta: { titleKey: 'associationAdmin.planTitle', requiresAuth: true, sidebar: 'sxaPlatform', superAdminOnly: true }
      },
      {
        path: 'admin/sxa/records', name: 'SxaActivationRecords', component: () => import('@/views/dashboard/SxaServiceRecordsView.vue'), meta: { titleKey: 'associationAdmin.recordsTitle', requiresAuth: true, sidebar: 'sxaPlatform', superAdminOnly: true }
      },
      {
        path: 'conference/votes/:id/edit',
        name: 'VotingEditor',
        component: () => import('@/views/conference/VotingEditorView.vue'),
        meta: { titleKey: 'route.votingManage', requiresAuth: true, permission: '查看投票详情' }
      },
      {
        // 会务系统 —— 报名管理
        path: 'conference/registrations',
        name: 'ConferenceRegistrations',
        component: () => import('@/views/conference/RegistrationManageView.vue'),
        meta: { titleKey: 'route.registrationManage', requiresAuth: true, permission: REGISTRATION_NAV_PERMISSIONS }
      },
      {
        // 会务系统 —— 报名设计器（业务主键 registrationId；含报名列表 Tab）
        path: 'conference/registrations/:registrationId/edit',
        name: 'RegistrationFormEdit',
        component: () => import('@/views/conference/RegistrationFormEditorView.vue'),
        meta: {
          titleKey: 'route.registrationFormEdit',
          requiresAuth: true,
          contentFill: 'inset',
          permission: [RBAC.REGISTRATION_EDIT, RBAC.PROXY_SUBMISSION_LIST, RBAC.SUBMISSION_LIST, RBAC.ORDER_LIST]
        }
      },
      {
        // 旧对接人工作台 URL → 报名编辑 · 报名列表 Tab
        path: 'conference/registrations/:registrationId/proxy',
        redirect: (to) => ({
          name: 'RegistrationFormEdit',
          params: { registrationId: to.params.registrationId },
          query: { tab: 'submissions' }
        })
      },
      {
        path: 'conference/registrations/:registrationId/proxy/registrations',
        redirect: (to) => ({
          name: 'RegistrationFormEdit',
          params: { registrationId: to.params.registrationId },
          query: { tab: 'submissions' }
        })
      },
      {
        path: 'conference/registrations/:registrationId/proxy/orders',
        redirect: (to) => ({
          name: 'RegistrationFormEdit',
          params: { registrationId: to.params.registrationId },
          query: { tab: 'orders' }
        })
      },
      {
        path: 'conference/registrations/:registrationId/proxy-entry',
        name: 'RegistrationProxyEntry',
        redirect: (to) => ({
          name: 'RegistrationFormEdit',
          params: { registrationId: to.params.registrationId },
          query: { tab: 'submissions' }
        })
      },
      {
        // 会务系统 —— 酒店管理
        path: 'conference/hotels',
        name: 'ConferenceHotels',
        component: () => import('@/views/conference/HotelManageView.vue'),
        meta: { titleKey: 'route.hotelManage', requiresAuth: true, permission: RBAC.HOTEL_LIST }
      },
      {
        // 新建相册 —— 相册信息（与编辑共用 AlbumInfoView.vue）
        path: 'albums/create',
        name: 'AlbumCreate',
        component: () => import('@/views/album/AlbumInfoView.vue'),
        meta: { titleKey: 'route.albumCreate', requiresAuth: true, sidebar: 'album', permission: RBAC.ALBUM_CREATE }
      },
      {
        // 旧创建 URL：-1 表示原「创建态」地址，重定向到新 /albums/create
        path: 'albums/-1/edit',
        redirect: (to) => ({ path: '/dashboard/albums/create', query: to.query, hash: to.hash })
      },
      {
        // 编辑相册信息
        path: 'albums/:id/edit',
        name: 'AlbumEdit',
        component: () => import('@/views/album/AlbumInfoView.vue'),
        meta: { titleKey: 'route.albumEdit', requiresAuth: true, sidebar: 'album', permission: RBAC.ALBUM_EDIT }
      },
      {
        // 相册内：图片管理（从列表「管理」进入）
        // :id 仅数字，避免与 /albums/create 等固定段冲突
        path: 'albums/:id(\\d+)/photos',
        name: 'AlbumPhotos',
        component: () => import('@/views/album/AlbumPhotosView.vue'),
        meta: { titleKey: 'route.photoManage', requiresAuth: true, sidebar: 'imageManage', permission: RBAC.ALBUM_PHOTOS_LIST }
      },
      {
        // 相册内：AI 修图体验（首页表格「修图」、顶栏快捷「修图」进入）
        path: 'albums/:id(\\d+)/retouch',
        name: 'AlbumRetouch',
        component: () => import('@/views/album/AiRetouchExperienceView.vue'),
        meta: {
          titleKey: 'route.aiRetouch',
          requiresAuth: true,
          hideSidebar: true,
          contentFill: true
        }
      },
      {
        // 编辑相册 —— 基础信息区
        path: 'albums/:id/edit/base-info',
        name: 'AlbumEditBaseInfo',
        component: () => import('@/views/album/AlbumBaseInfoView.vue'),
        meta: { titleKey: 'route.baseInfo', requiresAuth: true, sidebar: 'album' }
      },
      {
        // 新建相册 —— 隐私设置
        path: 'albums/create/private',
        name: 'AlbumCreatePrivate',
        component: () => import('@/views/album/AlbumPrivateView.vue'),
        meta: { titleKey: 'route.privacySettings', requiresAuth: true, sidebar: 'album' }
      },
      {
        // 新建相册 —— 分享设置（分享标题 / 分享图；接口待后端）
        path: 'albums/create/share',
        name: 'AlbumCreateShare',
        component: () => import('@/views/album/AlbumShareSettingView.vue'),
        meta: { titleKey: 'route.shareSettings', requiresAuth: true, sidebar: 'album' }
      },
      {
        // 新建相册 —— 基础信息区
        path: 'albums/create/base-info',
        name: 'AlbumCreateBaseInfo',
        component: () => import('@/views/album/AlbumBaseInfoView.vue'),
        meta: { titleKey: 'route.baseInfo', requiresAuth: true, sidebar: 'album' }
      },
      {
        // 新建相册 —— 顶部宣传位
        path: 'albums/create/top-promotion',
        name: 'AlbumCreateTopPromotion',
        component: () => import('@/views/album/TopPromotionView.vue'),
        meta: { titleKey: 'route.topPromotion', requiresAuth: true, sidebar: 'album' }
      },
      {
        // 原创建态隐私：重定向
        path: 'albums/-1/edit/private',
        redirect: (to) => ({
          path: '/dashboard/albums/create/private',
          query: to.query,
          hash: to.hash
        })
      },
      {
        // 编辑相册 —— 顶部宣传位
        path: 'albums/:id/edit/top-promotion',
        name: 'AlbumEditTopPromotion',
        component: () => import('@/views/album/TopPromotionView.vue'),
        meta: { titleKey: 'route.topPromotion', requiresAuth: true, sidebar: 'album' }
      },
      {
        // 新建相册 —— 启动页海报（设计稿 Web 1920 – 13 主内容）
        path: 'albums/create/launch-poster',
        name: 'AlbumCreateLaunchPoster',
        component: () => import('@/views/album/LaunchPosterView.vue'),
        meta: { titleKey: 'route.launchPoster', requiresAuth: true, sidebar: 'album' }
      },
      {
        // 编辑相册 —— 启动页海报
        path: 'albums/:id/edit/launch-poster',
        name: 'AlbumEditLaunchPoster',
        component: () => import('@/views/album/LaunchPosterView.vue'),
        meta: { titleKey: 'route.launchPoster', requiresAuth: true, sidebar: 'album' }
      },
      {
        // 新建相册 —— 加载图（设计稿 Web 1920 – 14 主内容）
        path: 'albums/create/loading-image',
        name: 'AlbumCreateLoadingImage',
        component: () => import('@/views/album/LoadingImageView.vue'),
        meta: { titleKey: 'route.loadingImage', requiresAuth: true, sidebar: 'album' }
      },
      {
        // 编辑相册 —— 加载图
        path: 'albums/:id/edit/loading-image',
        name: 'AlbumEditLoadingImage',
        component: () => import('@/views/album/LoadingImageView.vue'),
        meta: { titleKey: 'route.loadingImage', requiresAuth: true, sidebar: 'album' }
      },
      {
        // 新建相册 —— 基础广告（设计稿 Web 1920 – 22 主内容）
        path: 'albums/create/basic-ad',
        name: 'AlbumCreateBasicAd',
        component: () => import('@/views/album/BasicAdView.vue'),
        meta: { titleKey: 'route.basicAd', requiresAuth: true, sidebar: 'album' }
      },
      {
        // 编辑相册 —— 基础广告
        path: 'albums/:id/edit/basic-ad',
        name: 'AlbumEditBasicAd',
        component: () => import('@/views/album/BasicAdView.vue'),
        meta: { titleKey: 'route.basicAd', requiresAuth: true, sidebar: 'album' }
      },
      {
        // 新建相册 —— 底部推广栏（设计稿 Web 1920 – 27）
        path: 'albums/create/bottom-promotion-bar',
        name: 'AlbumCreateBottomPromotionBar',
        component: () => import('@/views/album/BottomPromotionBarView.vue'),
        meta: { titleKey: 'route.bottomPromotionBar', requiresAuth: true, sidebar: 'album' }
      },
      {
        // 编辑相册 —— 底部推广栏
        path: 'albums/:id/edit/bottom-promotion-bar',
        name: 'AlbumEditBottomPromotionBar',
        component: () => import('@/views/album/BottomPromotionBarView.vue'),
        meta: { titleKey: 'route.bottomPromotionBar', requiresAuth: true, sidebar: 'album' }
      },
      {
        // 新建相册 —— 批量水印（display_setting.watermark）
        path: 'albums/create/batch-watermark',
        name: 'AlbumCreateBatchWatermark',
        component: () => import('@/views/album/BatchWatermarkView.vue'),
        meta: { titleKey: 'route.batchWatermark', requiresAuth: true, sidebar: 'album' }
      },
      {
        // 编辑相册 —— 批量水印
        path: 'albums/:id/edit/batch-watermark',
        name: 'AlbumEditBatchWatermark',
        component: () => import('@/views/album/BatchWatermarkView.vue'),
        meta: { titleKey: 'route.batchWatermark', requiresAuth: true, sidebar: 'album' }
      },
      {
        // 新建相册 —— 侧边广告（设计稿 Web 1920 – 24，暂未对接保存接口）
        path: 'albums/create/side-ad',
        name: 'AlbumCreateSideAd',
        component: () => import('@/views/album/SideAdView.vue'),
        meta: { titleKey: 'route.sideAd', requiresAuth: true, sidebar: 'album' }
      },
      {
        // 编辑相册 —— 侧边广告
        path: 'albums/:id/edit/side-ad',
        name: 'AlbumEditSideAd',
        component: () => import('@/views/album/SideAdView.vue'),
        meta: { titleKey: 'route.sideAd', requiresAuth: true, sidebar: 'album' }
      },
      {
        // 新建相册 —— 信息卡片（设计稿 Web 1920 – 26）
        path: 'albums/create/info-card',
        name: 'AlbumCreateInfoCard',
        component: () => import('@/views/album/InfoCardView.vue'),
        meta: { titleKey: 'route.infoCard', requiresAuth: true, sidebar: 'album' }
      },
      {
        // 编辑相册 —— 信息卡片
        path: 'albums/:id/edit/info-card',
        name: 'AlbumEditInfoCard',
        component: () => import('@/views/album/InfoCardView.vue'),
        meta: { titleKey: 'route.infoCard', requiresAuth: true, sidebar: 'album' }
      },
      {
        // 新建相册 —— 大图详情页
        path: 'albums/create/large-image-detail',
        name: 'AlbumCreateLargeImageDetail',
        component: () => import('@/views/album/LargeImageDetailView.vue'),
        meta: { titleKey: 'route.largeImageDetail', requiresAuth: true, sidebar: 'album' }
      },
      {
        // 编辑相册 —— 大图详情页
        path: 'albums/:id/edit/large-image-detail',
        name: 'AlbumEditLargeImageDetail',
        component: () => import('@/views/album/LargeImageDetailView.vue'),
        meta: { titleKey: 'route.largeImageDetail', requiresAuth: true, sidebar: 'album' }
      },
      {
        // 编辑相册 —— 隐私设置
        path: 'albums/:id/edit/private',
        name: 'AlbumPrivateEdit',
        component: () => import('@/views/album/AlbumPrivateView.vue'),
        meta: { titleKey: 'route.privacySettings', requiresAuth: true, sidebar: 'album' }
      },
      {
        // 编辑相册 —— 分享设置
        path: 'albums/:id/edit/share',
        name: 'AlbumShareEdit',
        component: () => import('@/views/album/AlbumShareSettingView.vue'),
        meta: { titleKey: 'route.shareSettings', requiresAuth: true, sidebar: 'album' }
      },
      {
        // 旧「相册权限」页已并入默认工作流
        path: 'albums/create/album-permission',
        redirect: '/dashboard/albums/create/workflow'
      },
      {
        path: 'albums/:id/edit/album-permission',
        redirect: (to) => `/dashboard/albums/${to.params.id}/edit/workflow`
      },
      {
        // 摄影师工作台（独立页面，逻辑内联于 .vue）
        path: 'albums/:id(\\d+)/workbench/photographer',
        name: 'PhotographerWorkbench',
        component: () => import('@/views/album/workbench/PhotographerWorkbenchView.vue'),
        meta: { titleKey: 'route.photographerWorkbench', requiresAuth: true, sidebar: 'imageManage' }
      },
      {
        path: 'albums/:id(\\d+)/workbench/retouch',
        name: 'RetouchWorkbench',
        component: () => import('@/views/album/workbench/RetouchWorkbenchView.vue'),
        meta: { titleKey: 'route.retouchWorkbench', requiresAuth: true, sidebar: 'imageManage' }
      },
      {
        path: 'albums/:id(\\d+)/workbench/qc',
        name: 'QcWorkbench',
        component: () => import('@/views/album/workbench/QcWorkbenchView.vue'),
        meta: { titleKey: 'route.qcWorkbench', requiresAuth: true, sidebar: 'imageManage' }
      },
      {
        path: 'albums/:id(\\d+)/workbench/auditor',
        name: 'AuditorWorkbench',
        component: () => import('@/views/album/workbench/AuditorWorkbenchView.vue'),
        meta: { titleKey: 'route.auditorWorkbench', requiresAuth: true, sidebar: 'imageManage' }
      },
      {
        path: 'albums/create/work-area',
        name: 'AlbumCreateWorkArea',
        component: () => import('@/views/album/WorkAreaView.vue'),
        meta: { titleKey: 'route.workArea', requiresAuth: true, sidebar: 'album' }
      },
      {
        path: 'albums/:id/edit/work-area',
        name: 'AlbumEditWorkArea',
        component: () => import('@/views/album/WorkAreaView.vue'),
        meta: { titleKey: 'route.workArea', requiresAuth: true, sidebar: 'album' }
      },
      {
        path: 'albums/create/workflow',
        name: 'AlbumCreateDefaultWorkflow',
        component: () => import('@/views/album/DefaultWorkflowView.vue'),
        meta: { titleKey: 'route.defaultWorkflow', requiresAuth: true, sidebar: 'album' }
      },
      {
        path: 'albums/:id/edit/workflow',
        name: 'AlbumEditDefaultWorkflow',
        component: () => import('@/views/album/DefaultWorkflowView.vue'),
        meta: { titleKey: 'route.defaultWorkflow', requiresAuth: true, sidebar: 'album' }
      },
      {
        // 新建相册 —— 人员 · 相册图片（协作流程）
        path: 'albums/create/album-images',
        name: 'AlbumCreateAlbumImages',
        component: () => import('@/views/album/AlbumWorkflowImagesView.vue'),
        meta: { titleKey: 'route.albumImages', requiresAuth: true, sidebar: 'album' }
      },
      {
        // 编辑相册 —— 人员 · 相册图片（协作流程）
        path: 'albums/:id/edit/album-images',
        name: 'AlbumEditAlbumImages',
        component: () => import('@/views/album/AlbumWorkflowImagesView.vue'),
        meta: { titleKey: 'route.albumImages', requiresAuth: true, sidebar: 'album' }
      }
    ]
  },

  {
    path: '/h5/activity/:activityId/register/:registrationId/details',
    name: 'RegistrationH5DetailsWithActivity',
    component: () => import('@/registration/runtime/RegisterH5App.vue'),
    meta: { titleKey: 'route.registrationH5', requiresParticipantAuth: true, registrationDetails: true }
  },

  {
    path: '/h5/register/:registrationId/details',
    name: 'RegistrationH5Details',
    component: () => import('@/registration/runtime/RegisterH5App.vue'),
    meta: { titleKey: 'route.registrationH5', requiresParticipantAuth: true, registrationDetails: true }
  },

  {
    // 报名 H5（长期）：入口活动 id + registrationId，多活动绑同一表时靠路径区分
    path: '/h5/activity/:activityId/register/:registrationId',
    name: 'RegistrationH5WithActivity',
    component: () => import('@/registration/runtime/RegisterH5App.vue'),
    meta: { titleKey: 'route.registrationH5', requiresParticipantAuth: true }
  },

  {
    // 报名 H5 旧路径（兼容无活动上下文的复制链接；须 /h5/:pageId 之前）
    path: '/h5/register/:registrationId',
    name: 'RegistrationH5',
    component: () => import('@/registration/runtime/RegisterH5App.vue'),
    meta: { titleKey: 'route.registrationH5', requiresParticipantAuth: true }
  },

  {
    // 系统内置 · 会议日程 H5（须 /h5/:pageId 之前；按 activityId 拉会议列表）
    path: '/h5/agenda/:activityId',
    name: 'MicrositeAgendaH5',
    component: () => import('@/microsite/runtime/AgendaH5App.vue'),
    meta: { titleKey: 'route.micrositeAgendaH5' }
  },

  {
    path: '/h5/tasks/:activityId',
    name: 'ActivityTaskPeopleH5',
    component: () => import('@/conference/tasks/TaskPeopleH5App.vue'),
    meta: { titleKey: 'route.activityTaskPeopleH5' }
  },
  {
    path: '/h5/tasks/:activityId/:personId',
    name: 'ActivityTaskPeopleH5Detail',
    component: () => import('@/conference/tasks/TaskPeopleH5App.vue'),
    meta: { titleKey: 'route.activityTaskPeopleH5' }
  },

  {
    path: '/h5/hotel-query/:pageId',
    name: 'HotelQueryH5',
    component: () => import('@/conference/hotel/HotelQueryH5App.vue'),
    meta: { title: '酒店查询' }
  },
  {
    // 座位查询 H5（须 /h5/:pageId 之前）
    path: '/h5/seat-query/:pageId',
    name: 'SeatQueryH5',
    component: () => import('@/conference/seating/queryEditor/SeatQueryH5App.vue'),
    meta: { titleKey: 'route.seatQueryH5' }
  },

  {
    path: '/h5/checkin/:code/scan-desk',
    name: 'CheckinScanDeskH5',
    component: () => import('@/conference/checkin/h5/CheckinScanDeskH5.vue'),
    meta: { titleKey: 'route.checkinScanDeskH5' }
  },
  {
    path: '/h5/checkin/:code/print-desk',
    name: 'CheckinPrintDeskH5',
    component: () => import('@/conference/checkin/h5/CheckinPrintDeskH5.vue'),
    meta: { titleKey: 'route.checkinPrintDeskH5' }
  },
  {
    path: '/h5/checkin/:code/success',
    name: 'CheckinSuccessH5',
    component: () => import('@/conference/checkin/h5/CheckinSuccessH5.vue'),
    meta: { titleKey: 'route.checkinH5' }
  },
  {
    path: '/h5/checkin/:code/search',
    name: 'CheckinSearchH5',
    component: () => import('@/conference/checkin/h5/CheckinSearchH5.vue'),
    meta: { titleKey: 'route.checkinH5' }
  },
  {
    path: '/h5/checkin/:code/locate',
    name: 'CheckinLocateH5',
    component: () => import('@/conference/checkin/h5/CheckinLocateH5.vue'),
    meta: { titleKey: 'route.checkinH5' }
  },
  {
    path: '/h5/checkin/:code/scan',
    name: 'CheckinScanH5',
    component: () => import('@/conference/checkin/h5/CheckinScanH5.vue'),
    meta: { titleKey: 'route.checkinH5' }
  },
  {
    path: '/h5/checkin/:code',
    name: 'CheckinH5',
    component: () => import('@/conference/checkin/h5/CheckinH5Entry.vue'),
    meta: { titleKey: 'route.checkinH5' }
  },

  {
    path: '/h5/embedded-link',
    name: 'MicrositeEmbeddedLink',
    component: () => import('@/microsite/runtime/EmbeddedLinkPage.vue'),
    meta: { title: '半度云', appendAppName: false }
  },
  {
    // 会务微站 C 端 H5（公开访问，按 pageId 渲染）
    path: '/h5/:pageId',
    name: 'MicrositePage',
    component: () => import('@/microsite/runtime/H5App.vue'),
    meta: { titleKey: 'route.micrositeH5' }
  },

  { path: '/:pathMatch(.*)*', redirect: '/login' }
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes
})

/**
 * 全局前置守卫
 * - 未登录访问 requiresAuth 页面 → 跳转登录
 * - 已登录访问 guest 页面 → 跳转首页
 */
router.beforeEach((to, from) => {
  const token = localStorage.getItem('token')
  const participantToken = getParticipantToken()

  syncDocumentTitle(to)

  /** C 端 H5 需 participant Token（与 B 端 token 无关） */
  if (to.meta?.requiresParticipantAuth && !participantToken) {
    return { name: 'ParticipantLogin', query: { redirect: to.fullPath } }
  }

  /** B 端管理后台登录 */
  if (to.meta?.requiresAuth && !token) {
    return { name: 'Login', query: { redirect: to.fullPath } }
  }

  /** 超管专属路由（事项待办等） */
  if (token && to.meta?.superAdminOnly && !resolveIsSuperAdminFromStorage()) {
    return { name: 'Forbidden', query: { from: to.fullPath } }
  }

  /** RBAC 路由守卫 */
  if (
    token &&
    to.meta?.requiresAuth &&
    to.name !== 'Forbidden'
  ) {
    const required = resolveRoutePermissionMeta(to)
    if (required) {
      const names = getPermissionNamesFromStorage()
      /** 超管专属页：侧栏仅校验 isSuperAdmin，路由守卫同步放行（新 ACL 权限可能尚未写入角色） */
      const superAdminRouteBypass =
        resolveIsSuperAdminFromStorage() && isSuperAdminOnlyRoute(to)
      if (!superAdminRouteBypass && !checkRouteAccess(names, required)) {
        return { name: 'Forbidden', query: { from: to.fullPath } }
      }
    }
  }

  /** C 端已登录访问登录/注册页 → 回跳 redirect（relogin=1 时强制停留登录页以便换号） */
  if (to.meta?.participantGuest && participantToken && to.query.relogin !== '1') {
    const redirect = to.query.redirect
    if (typeof redirect === 'string' && redirect.startsWith('/')) {
      return redirect
    }
  }

  if (to.meta?.guest && token && !to.meta?.allowGuestWithAuth) {
    const redirect = to.query.redirect
    if (typeof redirect === 'string' && redirect.startsWith('/')) {
      return redirect
    }
    /** 已登录访问 login?token=：进登录页，由 LoginView 退出后走邀请流程 */
    if (to.name === 'Login' && String(to.query.token || '').trim()) {
      return
    }
    if (isStoredProxyUser()) {
      return resolvePostLoginRedirect({}, { isProxy: true })
    }
    return { name: 'Dashboard' }
  }

  /**
   * 新建向导：待关联的草稿相册 id 存在 sessionStorage（见 albumCreateGate）。
   * 从向导外进入「相册信息」首页 /albums/create 时清理，避免沿用上一次的 id；
   * 在 /dashboard/albums/create/* 内部切换回相册信息步时不清理，以免打断当前向导。
   */
  if (to.name === 'AlbumCreate') {
    const fromInCreateWizard = String(from.fullPath || '').startsWith('/dashboard/albums/create')
    if (!fromInCreateWizard) {
      clearPendingCreateAlbumId()
    }
  }

  /** 从列表进入新建活动向导时清理旧的 session 草稿 id */
  if (to.name === 'EventCreate') {
    const fromInCreateWizard = String(from.fullPath || '').startsWith('/dashboard/conference/events/create')
    if (!fromInCreateWizard) {
      clearPendingCreateActivityId()
    }
  }
})

router.afterEach((to) => {
  if (typeof document === 'undefined') return

  const isEmbeddedH5 =
    to.path.startsWith('/h5/') &&
    String(to.query.embed || '') === '1'

  document.documentElement.classList.toggle(
    'microsite-h5-embed',
    isEmbeddedH5
  )
})

export default router
